import { useCallback, useMemo, useRef, useState } from 'react';
import { advanceSearch } from '../../hooks/socket/cameras';

export const useSearch = ({
  searchMethod = (input, page) => Promise.resolve([]),
}) => {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const page = useRef(0);
  const inputRef = useRef('');

  const search = useCallback(async (input) => {
    inputRef.current = input;
    page.current = 0;
    setIsLoading(true);

    try {
      const result = await searchMethod(input, 0);
      setPages([result]);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
      }
    },
    [searchMethod]
  );

  const loadMore = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const nextPage = page.current + 1;
      const result = await searchMethod(inputRef.current, nextPage);
      setPages((prev) => [...prev, result]);
      page.current = nextPage;
    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    pages,
    isLoading,
    loadMore,
    search,
  };
};

const unique = (list) => {
  return Array.from(new Map(list.map((item) => [item.id, item])).values());
};

const mergeData = (prev, next) => {
  return {
    ...next,
    records: {
      TbDeviceGroup: unique([
        ...prev.records.TbDeviceGroup,
        ...next.records.TbDeviceGroup,
      ]),
    },
  };
};

export const useAdvanceSearchV2 = ({ namespace }) => {
  const timeRequest = useRef(Date.now());

  const advanceSearchMethod = useCallback((input, page) => {
    return advanceSearch({
      input,
      page,
      namespace,
      applicationType: 'device-group',
      size: 10,
      timeRequest: timeRequest.current,
      });
    },
    [namespace]
  );

  const { pages, isLoading, loadMore, search } = useSearch({
    searchMethod: advanceSearchMethod,
    mergeData,
  });

  const data = useMemo(() => {
    if (!pages.length) return undefined;

    return pages.reduce((acc, page) => {
      return mergeData(acc, page);
    });
  }, [pages]);

  const canLoadMore = useMemo(() => {
    if (!data) return false;

    const { page, size, totalRecords } = data;

    return (page + 1) * size < totalRecords;
  }, [data]);

  return {
    data,
    isLoading,
    loadMore,
    search,
    canLoadMore,
  };
};
